import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';

export type RequestIdPrefixProvider = () => string | undefined;

export const REQUEST_ID_PREFIX = new InjectionToken<RequestIdPrefixProvider>('REQUEST_ID_PREFIX');

function randomId(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, byte => (byte % 36).toString(36)).join('');
}

/** x-request-id "<prefix>.<random>", so all requests of one session share a prefix. */
@Injectable()
export class RequestIdInterceptor implements HttpInterceptor {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly prefix = inject(REQUEST_ID_PREFIX);
  private readonly fallbackPrefix = randomId(16);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.authenticationService.isAuthenticationUrl(request.url)) {
      return next.handle(request);
    }
    const prefix = this.prefix() ?? this.fallbackPrefix;
    return next.handle(
      request.clone({ setHeaders: { 'x-request-id': `${prefix}.${randomId(8)}` } })
    );
  }
}

/** Opt-in. The factory runs in an injection context. */
export function provideRequestId(prefixFactory: () => RequestIdPrefixProvider): Provider[] {
  return [
    { provide: REQUEST_ID_PREFIX, useFactory: prefixFactory },
    { provide: HTTP_INTERCEPTORS, useClass: RequestIdInterceptor, multi: true }
  ];
}
